import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef } from "react";
import { useAppContext } from "../../../../../AppContext";
import { IEventWizardInitialData } from "../../models/eventWizardInitialDataModel";
import { PageMode } from "../../../../../common/types";
import { useParams } from "react-router";
import { useFormValidationMessages } from "../../../../../common/infrastructure/hooks/useFormHelper";
import {
  IBlockedTimeWizardFormModel,
  blockedTimeWizardFormDefaultValues,
  blockedTimeWizardFormSchema,
} from "../models/blockedTimeWizardFormModel";
import { IEmployeeWithAvailabilityModel } from "../../../../../common/components/forms/inputs/instructorCalendar/models/employeeCalendarModels";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { EventType } from "../../../../../common/enums";
import { IBreakTypeSelectModel } from "../models/breakTypeSelectModel";
import { isFullDayBlockedTime } from "../utils/fullDayBlockedTimeUtility";

export const useBlockedTimeWizardForm = (
  selectedDate?: Date,
  studioEmployees?: IEmployeeWithAvailabilityModel[],
  initialData?: IEventWizardInitialData,
  pageMode?: PageMode
) => {
  const { selectedStudioId, currentUser } = useAppContext();
  const { id } = useParams();
  const isInitializedRef = useRef(false);

  const form = useForm<IBlockedTimeWizardFormModel>({
    defaultValues: blockedTimeWizardFormDefaultValues,
    resolver: yupResolver(blockedTimeWizardFormSchema) as any,
  });

  const { data: breakTypes, isLoading: isBreakTypesLoading } = useApiQuery<
    IBreakTypeSelectModel[]
  >({
    queryKey: [endpointsMetadata.breakTypeNomenclature.getAll],
    endpoint: endpointsMetadata.breakTypeNomenclature.getAll,
    enabled: true,
    globalIsLoading: false,
  });

  const breakTypeId = form.watch("breakTypeId");

  useEffect(() => {
    if (breakTypes && breakTypeId && breakTypeId > 0) {
      const selectedBreakType = breakTypes.find((bt) => bt.id === breakTypeId);
      if (selectedBreakType && selectedBreakType.durationInMinutes) {
        form.setValue("durationInMinutes", selectedBreakType.durationInMinutes);
        const currentDate = form.getValues("date");
        if (
          currentDate &&
          pageMode !== "edit" &&
          isInitializedRef.current &&
          !form.getValues("isFullDay")
        ) {
          form.setValue("date", undefined);
        }
      }
    }
  }, [breakTypeId, breakTypes, form, pageMode]);

  useEffect(() => {
    if (pageMode !== "edit" && studioEmployees && studioEmployees.length > 0) {
      const currentEmployeeId = form.getValues("employeeId");
      if (!currentEmployeeId) {
        const currentUserEmployee = studioEmployees.find(
          (emp) => emp.id === currentUser?.id
        );
        const defaultEmployeeId = currentUserEmployee
          ? currentUserEmployee.id
          : studioEmployees[0].id;
        form.setValue("employeeId", defaultEmployeeId);
      }
    }

    // Set default break type (first one after loading)
    if (
      pageMode !== "edit" &&
      breakTypes &&
      breakTypes.length > 0 &&
      !form.getValues("breakTypeId")
    ) {
      form.setValue("breakTypeId", breakTypes[0].id);
    }

    // Handle edit mode - populate from existing data
    if (initialData?.currentlySelectedEvent) {
      if (pageMode !== "duplicate") {
        form.setValue("id", Number(id));
      }

      form.setValue(
        "breakTypeId",
        initialData.currentlySelectedEvent.breakTypeId || 0
      );
      form.setValue(
        "employeeId",
        initialData.currentlySelectedEvent.employeeId || 0
      );
      form.setValue(
        "durationInMinutes",
        initialData.currentlySelectedEvent.durationInMinutes
      );
      form.setValue(
        "date",
        new Date(initialData.currentlySelectedEvent.startTime)
      );
      form.setValue(
        "isRecurring",
        initialData.currentlySelectedEvent.isRecurring
      );
      form.setValue(
        "isFullDay",
        isFullDayBlockedTime(
          new Date(initialData.currentlySelectedEvent.startTime),
          initialData.currentlySelectedEvent.durationInMinutes
        )
      );
    } else if (selectedDate && pageMode !== "add") {
      // Only set date in non-add modes (like duplicate)
      form.setValue("date", selectedDate);
    }

    // Always set studio ID and type
    if (selectedStudioId) {
      form.setValue("studioId", selectedStudioId);
    }
    form.setValue("type", EventType.BlockedTime);

    // Mark as initialized after all initial values are set
    isInitializedRef.current = true;
  }, [
    selectedStudioId,
    selectedDate,
    initialData,
    studioEmployees,
    pageMode,
    id,
    breakTypes,
    form,
  ]);

  useFormValidationMessages<IBlockedTimeWizardFormModel>(form);

  return {
    form,
    breakTypes,
    isBreakTypesLoading,
  };
};
